import { Search, X, Grid, List, ChevronLeft } from "lucide-react";
import { useState } from "react";

export function MenuSection({ businessCatalog }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'


  const catalog = businessCatalog[0];

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleExploreCatalog = () => {
    setIsModalOpen(true);
    setSelectedItem(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const goBackToList = () => {
    setSelectedItem(null);
  };

  return (
    <>
      <div className="p-4 sm:p-6 border-b border-[#F5E8EA]">
        <h2 className="text-[20px] font-bold text-[#232123] mb-4">Catalog</h2>

        {/* Catalog Card */}
        <div className="border border-[#F5E8EA] rounded-xl overflow-hidden max-w-[400px]">
          <div className="flex p-3">
            {/* Left side - Catalog Image */}
            <div className="relative w-[112px] h-[112px] flex-shrink-0">
              <img
                src={catalog?.catalog_image_url || "/image/business.png"}
                alt="Catalog"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>

            {/* Right side - Catalog Items */}
            <div className="flex-1 ml-3">
              <h3 className="text-[15px] text-[#4f4d4f] mb-2">Popular Items</h3>

              {/* Items Grid - Clickable */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-2">
                {catalog?.items?.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleItemClick(item)}
                    className="flex items-center hover:bg-[#F5E8EA] rounded px-1 py-0.5 transition-colors duration-200 text-left"
                  >
                    <span className="text-[#232123] mr-1.5">•</span>
                    <span className="text-[13px] text-[#232123] truncate">
                      {item.item_name}
                    </span>
                  </button>
                ))}
              </div>

              {/* Explore Catalog Link */}
              <button
                onClick={handleExploreCatalog}
                className="flex items-center text-[13px] hover:bg-[#F5E8EA] rounded px-1 py-0.5 transition-colors duration-200"
              >
                <Search className="h-3.5 w-3.5 mr-1.5 text-[#7b189f]" />
                <span className="text-[#7b189f] font-medium">Explore Menu</span>
                <span className="mx-1.5 text-[#4f4d4f]">·</span>
                <span className="text-[#4f4d4f]">
                  {catalog?.items?.length || 0} items
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Catalog Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#F5E8EA] bg-gradient-to-r from-[#7b189f] to-[#9c4221] text-white">
              <div className="flex items-center space-x-3">
                {selectedItem && (
                  <button
                    onClick={goBackToList}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors duration-200"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                <h3 className="text-[18px] font-bold">
                  {selectedItem ? selectedItem.item_name : "Full Catalog"}
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                {!selectedItem && (
                  <div className="flex bg-white bg-opacity-20 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-1.5 rounded transition-colors duration-200 ${
                        viewMode === "grid"
                          ? "bg-white text-[#7b189f]"
                          : "text-white hover:bg-white hover:bg-opacity-30"
                      }`}
                    >
                      <Grid className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-1.5 rounded transition-colors duration-200 ${
                        viewMode === "list"
                          ? "bg-white text-[#7b189f]"
                          : "text-white hover:bg-white hover:bg-opacity-30"
                      }`}
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <button
                  onClick={closeModal}
                  className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors duration-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div
              className="overflow-y-auto"
              style={{ maxHeight: "calc(85vh - 80px)" }}
            >
              {selectedItem ? (
                /* Single Item Detail View */
                <div className="p-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-[24px] font-bold text-[#232123] mb-2">
                        {selectedItem.item_name}
                      </h4>
                      {selectedItem.price && (
                        <div className="inline-block bg-gradient-to-r from-[#7b189f] to-[#9c4221] text-white px-4 py-2 rounded-lg">
                          <span className="text-[18px] font-bold">
                            ${selectedItem.price}
                          </span>
                        </div>
                      )}
                    </div>

                    {selectedItem.description && (
                      <div className="bg-[#F5E8EA] rounded-xl p-4">
                        <h5 className="text-[14px] font-semibold text-[#232123] mb-2">
                          Description
                        </h5>
                        <p className="text-[14px] text-[#4f4d4f] leading-relaxed">
                          {selectedItem.description}
                        </p>
                      </div>
                    )}

                    {/* Display catalog image as reference */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h5 className="text-[14px] font-semibold text-[#232123] mb-3">
                        From Our Catalog
                      </h5>
                      <img
                        src={
                          catalog?.catalog_image_url || "/image/business.png"
                        }
                        alt="Business Catalog"
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Full Catalog View */
                <div className="p-6">
                  {/* Catalog Overview Image */}
                  <div className="mb-6">
                    <div className="w-full h-48 bg-gradient-to-br from-[#F5E8EA] to-[#E8D5D7] rounded-xl overflow-hidden">
                      <img
                        src={
                          catalog?.catalog_image_url || "/image/business.png"
                        }
                        alt="Business Catalog"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="text-[18px] font-bold text-[#232123]">
                      All Items ({catalog?.items?.length || 0})
                    </h4>
                  </div>

                  {viewMode === "grid" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {catalog?.items?.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedItem(item)}
                          className="bg-white border border-[#F5E8EA] hover:border-[#7b189f] rounded-xl p-4 transition-all duration-200 text-left hover:shadow-lg group"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h5 className="text-[16px] font-semibold text-[#232123] group-hover:text-[#7b189f] transition-colors duration-200">
                              {item.item_name}
                            </h5>
                            {item.price && (
                              <span className="text-[14px] font-bold text-[#7b189f] bg-[#F5E8EA] px-2 py-1 rounded-lg">
                                ${item.price}
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-[13px] text-[#4f4d4f] line-clamp-2 leading-relaxed">
                              {item.description}
                            </p>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {catalog?.items?.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedItem(item)}
                          className="w-full flex items-center justify-between p-4 hover:bg-[#F5E8EA] rounded-xl transition-colors duration-200 text-left border border-transparent hover:border-[#7b189f]"
                        >
                          <div className="flex-1 min-w-0">
                            <h5 className="text-[15px] font-semibold text-[#232123] mb-1">
                              {item.item_name}
                            </h5>
                            {item.description && (
                              <p className="text-[13px] text-[#4f4d4f] truncate">
                                {item.description}
                              </p>
                            )}
                          </div>
                          {item.price && (
                            <div className="ml-4 flex-shrink-0">
                              <span className="text-[16px] font-bold text-[#7b189f] bg-[#F5E8EA] px-3 py-1 rounded-lg">
                                ${item.price}
                              </span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
